import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import * as NextRouter from 'next/router';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import ItemListingCard from './ItemListingCard';
import {
  SLMainBoxContainer,
  StItemListingGrid,
  StListingTitle,
  StPagination,
  StPaginationFooter,
  StShowingOfText,
} from './ItemListing.style';
import { STCRWrapper } from '../shared/Container/Container.styles';
import {
  ItemListingContentType,
  ItemListingProps,
  ItemListingSitecoreItem,
} from './ItemListing.type';
import { mapItemListingCards, resolveItemListingContentType } from './mapItemListingCard';
import FilterChips from '../shared/FilterChips/FilterChips';
import { ALL_ARTICLES_LABEL, ALL_STUDIES_LABEL } from '../shared/FilterChips/FilterChips.types';
import {
  getArticleConditionLabel,
  getArticleNavigateHref,
  getStudyConditionLabel,
  getStudyNavigateHref,
} from '../shared/FilterChips/FilterChips.utils';
import { formatShowingOf } from '../shared/Carousel/Carousel.utils';

const DESKTOP_PAGE_SIZE = 9;
const TABLET_MOBILE_PAGE_SIZE = 6;

function getLimitValue(field?: { value?: number | string }, fallback = 0): number {
  const raw = field?.value;
  if (raw === undefined || raw === null || raw === '') return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getCurrentPath(asPath?: string, pathQuery?: string | string[]): string {
  if (asPath) return asPath;
  if (Array.isArray(pathQuery)) return `/${pathQuery.join('/')}`;
  if (typeof pathQuery === 'string' && pathQuery) return `/${pathQuery}`;
  if (typeof window !== 'undefined') return window.location.pathname || '';
  return '';
}

function ItemListing(props: ItemListingProps): JSX.Element {
  const router = NextRouter.useRouter();
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const listingRef = useRef<HTMLDivElement>(null);
  const fields = props.fields;
  const listParam = props.params?.list;
  const studyList = fields?.studyList || [];
  const articleList = fields?.articleList || [];
  // itemList is retained on the Sitecore template for future pages; not rendered yet.
  const currentPath = getCurrentPath(router.asPath, router.query?.path);
  const contentType = resolveItemListingContentType(currentPath, listParam);
  const showFilterChips = Boolean(fields?.enableFilterChip?.value);
  const listingTitle = fields?.title;
  const showingOfTemplate = fields?.showingOf?.value;
  const pageSize = isTabletOrMobile ? TABLET_MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;

  const sourceList = useMemo((): ItemListingSitecoreItem[] => {
    if (contentType === ItemListingContentType.Study) return studyList;
    if (contentType === ItemListingContentType.Article) return articleList;
    return [];
  }, [contentType, studyList, articleList]);

  const [filteredRawList, setFilteredRawList] = useState<ItemListingSitecoreItem[]>(sourceList);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilteredRawList(sourceList);
  }, [sourceList]);

  useEffect(() => {
    setPage(1);
  }, [filteredRawList, contentType, pageSize]);

  const cards = useMemo(() => {
    if (!contentType) return [];
    return mapItemListingCards(filteredRawList, contentType);
  }, [contentType, filteredRawList]);

  const showPagination = cards.length > pageSize;

  const pageCount = showPagination ? Math.ceil(cards.length / pageSize) : 1;
  const pagedCards = useMemo(() => {
    if (!showPagination) return cards;
    const start = (page - 1) * pageSize;
    return cards.slice(start, start + pageSize);
  }, [cards, page, pageSize, showPagination]);

  const showingOfText =
    showPagination && showingOfTemplate
      ? formatShowingOf(showingOfTemplate, pagedCards.length, cards.length)
      : null;

  function handlePageChange(_event: ChangeEvent<unknown>, nextPage: number): void {
    setPage(nextPage);
    if (typeof listingRef.current?.scrollIntoView === 'function') {
      listingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  if (!fields) return <></>;

  const isStudyListing = contentType === ItemListingContentType.Study;
  const isArticleListing = contentType === ItemListingContentType.Article;

  return (
    <SLMainBoxContainer ref={listingRef}>
      <STCRWrapper>
        <StItemListingGrid>
          {listingTitle?.value && (
            <StListingTitle variant="h1" sx={{ marginBottom: '1.5rem' }}>
              <Text field={listingTitle} />
            </StListingTitle>
          )}
          {showFilterChips && isStudyListing && (
            <FilterChips
              items={studyList}
              onFilterChange={setFilteredRawList}
              getConditionLabel={getStudyConditionLabel}
              getNavigateHref={getStudyNavigateHref}
              allLabel={ALL_STUDIES_LABEL}
              align="start"
            />
          )}
          {showFilterChips && isArticleListing && (
            <FilterChips
              items={articleList}
              onFilterChange={setFilteredRawList}
              getConditionLabel={getArticleConditionLabel}
              getNavigateHref={getArticleNavigateHref}
              allLabel={ALL_ARTICLES_LABEL}
              align="start"
            />
          )}
          <Grid container rowGap="24px" columnSpacing={{ xs: 0, sm: '24px' }}>
            {pagedCards.map((card) => (
              <Grid item xs={12} sm={6} lg={4} key={card.id}>
                <ItemListingCard
                  card={card}
                  descriptionWordLimit={getLimitValue(fields.descriptionWordLimit, 30)}
                  titleCharacterLimit={getLimitValue(fields.titleCharacterLimit, 30)}
                  titleLineLimit={getLimitValue(fields.titleLineLimit, 2)}
                  descriptionLineLimit={getLimitValue(fields.descriptionLineLimit, 3)}
                />
              </Grid>
            ))}
          </Grid>
          {showPagination && (
            <StPaginationFooter data-testid="item-listing-pagination">
              <StPagination
                count={pageCount}
                page={page}
                onChange={handlePageChange}
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
              />
              {showingOfText && (
                <StShowingOfText data-testid="item-listing-showing-of">
                  {showingOfText}
                </StShowingOfText>
              )}
            </StPaginationFooter>
          )}
        </StItemListingGrid>
      </STCRWrapper>
    </SLMainBoxContainer>
  );
}

export default ItemListing;
